/* CompanyOverview · /company — the polished business overview page,
 * designed for sponsor-bank diligence + investor outreach.
 *
 * Matches the marketing site visual language (lime accents, dark cards,
 * eyebrow labels, the same MarketingMobileNav). Sections mirror the
 * banking-docs `00_business_overview.md` but with proper visual hierarchy
 * so it reads like a one-pager you can hand to a bank or VC.
 *
 * The `[NEEDS DATA]` placeholders in the underlying markdown become
 * clearly-marked "to fill in" pills here so verqo leadership can populate
 * them at their own pace without breaking the page.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CircleDot, CheckCircle2, Banknote, ShieldCheck, Eye,
  Layers, Zap, Network, FileText, Download, Building2, Globe, Plug,
  TrendingUp, Users, Sparkles, Target, Lock, KeyRound, UserCheck,
  RotateCcw, LineChart,
} from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import MarketingMobileNav from "../components/MarketingMobileNav";
import FlowOfFundsDiagram from "../components/FlowOfFundsDiagram";
import BuyerCardFlowDiagram from "../components/BuyerCardFlowDiagram";

// ── Shared atoms ──────────────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500"/>
    {children}
  </div>
);

const NeedsData = ({ children }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
    Needs data · {children}
  </span>
);

// ── Sections ──────────────────────────────────────────────────────────────
const SURFACE_AREA = [
  { icon: Banknote,    label: "D+1 supplier payouts",        body: "Sellers paid Day 1 — we carry the float for Net-30/60/90 buyers" },
  { icon: ShieldCheck, label: "Allianz invoice insurance",   body: "Bad-debt cover on every lender-funded invoice" },
  { icon: Eye,         label: "Buyer 360° monitoring",        body: "Real-time health, performance, payment history on every buyer" },
  { icon: Zap,         label: "60-second AI credit",          body: "Underwrite a new buyer without paper trade references" },
  { icon: Network,     label: "Wholesale OS",                 body: "Orders, invoices, payments, shipping, returns — one portal" },
  { icon: Layers,      label: "Dropship infrastructure",       body: "Catalog sync · 1-click import · auto-routing · daily invoices" },
];

const PARTNERSHIPS = [
  { name: "U.S. Bank",            role: "Per-counterparty DDAs + ACH/RTP/Wire origination",  status: "in integration" },
  { name: "Cloudflare",          role: "Edge security · zero-trust authentication · DDoS protection · DNS",  status: "live" },
  { name: "Allianz Trade",       role: "Trade-credit insurance on lender-funded invoices", status: "live" },
  { name: "Plaid",               role: "Bank-account verification (read-only)",            status: "live" },
  { name: "Twilio Verify",       role: "SMS MFA + phone verification",                     status: "live" },
  { name: "Resend",              role: "Transactional + marketing email",                  status: "live" },
  { name: "Middesk",             role: "KYB / business verification",                      status: "live" },
  { name: "Shopify · Woo · BigCommerce", role: "Catalog + order sync for dropship",        status: "live" },
  { name: "Verqo Shipping",   role: "Shipping rates + tracking",                        status: "live" },
];

// ── Page ──────────────────────────────────────────────────────────────────
export default function CompanyOverview() {
  useEffect(() => {
    document.title = "Verqo · Sponsor-bank diligence";
    // Sponsor-bank diligence portal must not be indexed by search engines —
    // private content intended for banking partners we directly link to.
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    const prev = m.getAttribute("content");
    m.setAttribute("content", "noindex, nofollow, noarchive");
    return () => {
      if (prev) m.setAttribute("content", prev); else m.remove();
      document.title = "verqo · The wholesale OS";
    };
  }, []);

  return (
    <div className="min-h-screen text-slate-900 antialiased selection:bg-lime-300" style={{ background: "#FAFAF7" }} data-testid="company-overview">
      {/* ─── DILIGENCE PORTAL HEADER ─── (stripped of marketing nav so banks
          aren't pulled into the seller / buyer funnels) */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/company" className="inline-flex items-center group" aria-label="Verqo">
              <VerqoLockup/>
            </Link>
            <span className="hidden md:inline-flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.22em] text-lime-700 font-semibold border-l border-slate-200 pl-4">
              <CircleDot className="w-2 h-2 fill-lime-500 text-lime-500"/>
              Sponsor-bank diligence portal
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-[12px] text-slate-600">
            <a href="#product"    className="hover:text-slate-900 transition-colors">Product</a>
            <a href="#flow"       className="hover:text-slate-900 transition-colors">Flow of funds</a>
            <a href="#security"   className="hover:text-slate-900 transition-colors">Security</a>
            <a href="#projections" className="hover:text-slate-900 transition-colors">Projections</a>
            <a href="#funding"    className="hover:text-slate-900 transition-colors">Funding</a>
            <a href="#team"       className="hover:text-slate-900 transition-colors">Team</a>
            <a href="#partners"   className="hover:text-slate-900 transition-colors">Partners</a>
            <Link to="/banking-docs" className="hover:text-slate-900 transition-colors">Full doc package</Link>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="/api/banking-docs/download-all"
              className="inline-flex items-center gap-1.5 text-[12px] text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded border border-slate-300 hover:bg-white"
              data-testid="header-download-zip"
            >
              <Download className="w-3.5 h-3.5"/> Download
            </a>
            <MarketingMobileNav links={[
              { label: "Product",   href: "#product" },
              { label: "Flow of funds",  href: "#flow" },
              { label: "Funding",   href: "#funding" },
              { label: "Team",      href: "#team" },
              { label: "Partners",  href: "#partners" },
              { label: "Full doc package", href: "/banking-docs" },
            ]} cta={{ label: "Download package", href: "/api/banking-docs/download-all" }}/>
          </div>
        </div>
        {/* Confidentiality strip — only visible at the very top of the page */}
        <div className="bg-amber-50/70 border-t border-amber-200/60">
          <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-1.5 text-center">
            <span className="text-[10.5px] mono uppercase tracking-[0.22em] text-amber-900 font-semibold">
              Confidential · For sponsor-bank review only · Do not distribute
            </span>
          </div>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="relative pt-36 md:pt-44 pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Sponsor-bank diligence · prepared Jun 2026</Eyebrow>
        <h1 className="mt-5 text-[40px] sm:text-[52px] md:text-[72px] leading-[0.98] tracking-tight font-semibold text-slate-900 max-w-3xl">
          The operating system for <span className="bg-lime-300 px-3 -rotate-1 inline-block">B2B wholesale.</span>
        </h1>
        <p className="mt-7 max-w-2xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
          A unified portal that gives brands selling wholesale and the retailers buying from them everything they need in one place — <strong className="text-slate-900">cashflow, risk coverage, buyer intelligence, automation, and dropship.</strong>
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a href="/api/banking-docs/download-all"
             className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all bg-[#0c1612]"
             data-testid="download-zip">
            Download diligence package
            <Download className="w-4 h-4"/>
          </a>
          <Link to="/banking-docs"
             className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50">
            Browse all docs <FileText className="w-4 h-4"/>
          </Link>
        </div>
      </section>

      {/* ─── PRODUCT OVERVIEW ─── */}
      <section id="product" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Product overview</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          Six things, one portal.
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          Verqo collapses the wholesale stack — financing, insurance, monitoring, ops, dropship — into a single seller portal, buyer portal, and admin control center.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SURFACE_AREA.map(p => (
            <div key={p.label} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3">
                <p.icon className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="text-[15px] font-semibold text-slate-900">{p.label}</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500">Seller portal</div>
            <div className="font-semibold text-slate-900 mt-1">Orders · invoices · wallet · credit · insurance · team</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500">Buyer portal</div>
            <div className="font-semibold text-slate-900 mt-1">Orders · payments · credit line · supplier directory</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500">Admin / Compliance</div>
            <div className="font-semibold text-slate-900 mt-1">KYB/KYC · financial controls · ops · audit chain</div>
          </div>
        </div>
      </section>

      {/* ─── FLOW OF FUNDS DIAGRAM ─── */}
      <section id="flow" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Flow of funds</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          How money moves through verqo.
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          A dedicated U.S. Bank DDA for every counterparty · ACH/RTP/Wire origination · six bank-issued virtual DDAs, segregated at the bank · external banks for every counterparty. No customer funds touch Verqo's operating account.
        </p>
        <div className="mt-10">
          <FlowOfFundsDiagram/>
        </div>
        <div className="mt-6">
          <BuyerCardFlowDiagram/>
        </div>
      </section>

      {/* ─── TRACTION ─── (removed Jun 2026 per founder — pre-revenue stage,
            no production data to show yet. Will re-add once live volume
            crosses a meaningful threshold.) */}

      {/* ─── SECURITY CONTROLS ─── */}
      <section id="security" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Security controls · every transaction</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          5 layers between an invoice and a dollar moving.
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          Wholesale B2B invoices are all "high-ticket" by definition. Verqo treats every money movement as a regulated event — the same scrutiny on $500 as on $50,000, with extra friction for the largest.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { n: 1, icon: KeyRound,    title: "Authenticated session",       body: "Every API call requires a valid JWT. Buyers, sellers, team members, admins — all gated identically. Sessions expire on a strict TTL." },
            { n: 2, icon: ShieldCheck, title: "SMS to owner's phone",         body: "Every payment, every money movement — regardless of amount or role — sends a fresh OTP to the TENANT OWNER's phone. Operators must obtain the code from the owner. Single-use, expires in 5 min." },
            { n: 3, icon: UserCheck,   title: "Co-approval > $25K",           body: "Payments at or above the configurable threshold (default $25K) require a SECOND owner/admin to sign off. Wallet held in escrow until approved. Configurable from Seller Settings → Payment Approvals." },
            { n: 4, icon: RotateCcw,   title: "30-second undo window",        body: "Even after a successful payment, the buyer has 30 seconds to reverse it. Wallet move atomically rolled back, invoice flipped back to unpaid, full audit row written." },
            { n: 5, icon: Lock,        title: "Hash-chained audit trail",     body: "Every state-changing event written to a tamper-evident hash chain (`audit_log` + `_audit_chain_state` collections). SOC 2-ready. Daily 2-way reconciliation against the sponsor bank's ledger." },
            { n: 6, icon: Network,     title: "Anomaly + suspicious activity", body: "Velocity caps per tenant · new-supplier detection · suspended-account auto-block · ROW MD5-hashed for tampering · sanction screening on every account opening." },
          ].map(item => (
            <div key={item.n} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-lime-300 grid place-items-center">
                  <item.icon className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <span className="text-[10.5px] mono uppercase tracking-wider text-slate-400">layer {item.n}</span>
              </div>
              <div className="text-[15px] font-semibold text-slate-900 leading-tight">{item.title}</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{item.body}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border-2 border-amber-300 bg-amber-50/40 p-5 md:p-6 flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-1"/>
          <div className="text-[13px] text-amber-900 leading-relaxed">
            <strong>Effective control:</strong> Even a compromised operator account cannot move money. The SMS routes to the owner's phone (not the initiator's), and amounts above $25K cannot complete without a second human reviewing the request. Insider fraud, social engineering, and stolen-device scenarios all hit the same gate.
          </div>
        </div>
      </section>

      {/* ─── PROJECTIONS section moved to AFTER Partners (see below) ─── */}

      {/* ─── FUNDING (bootstrap-backed) ─── */}
      <section id="funding" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Funding</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          Fully bootstrapped. <span className="text-slate-400">$10M family-backed.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          Verqo is built without venture capital. The founding team has a <strong className="text-slate-900">$10M internal family commitment</strong> ready to inject as growth requires — no outside dilution, no fundraising cycles, no investor reporting overhead.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border-2 border-lime-400 bg-lime-50/40 p-6">
            <div className="text-[10.5px] mono uppercase tracking-wider text-lime-700 font-semibold">Capital structure</div>
            <div className="text-2xl font-semibold text-slate-900 tracking-tight mt-2">100% bootstrapped</div>
            <div className="text-[12.5px] text-slate-700 mt-2 leading-relaxed">
              No VC, no preferred stock, no convertible notes. The cap table is the founding team.
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500 font-semibold">Standby capital</div>
            <div className="text-2xl font-semibold text-slate-900 tracking-tight mt-2 tabular-nums">$10,000,000</div>
            <div className="text-[12.5px] text-slate-700 mt-2 leading-relaxed">
              Internal family-fund commitment available on demand to fund growth, infrastructure, and capital requirements.
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500 font-semibold">Runway posture</div>
            <div className="text-2xl font-semibold text-slate-900 tracking-tight mt-2">Indefinite</div>
            <div className="text-[12.5px] text-slate-700 mt-2 leading-relaxed">
              Capital tranches drawn against operating need — never against a fundraise timeline.
            </div>
          </div>
        </div>

        <p className="mt-8 text-[12.5px] text-slate-500 max-w-2xl leading-relaxed">
          Why it matters to a sponsor bank: verqo isn't dependent on a fundraise to meet obligations. The $10M family commitment is documented and available on-demand, which means lower counterparty risk on platform-side balances.
        </p>
      </section>

      {/* ─── TEAM ─── */}
      <section id="team" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Leadership team</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          The people behind verqo.
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          A lean, hand-picked operating team — founders carrying the P&amp;L, not consultants reading from a deck. Every leader has lived in wholesale long enough to know what's broken and why.
        </p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { role: "Founder", title: "Zev Ostreicher",    sub: "Founder &amp; CEO",
              body: "Sets product direction, capital strategy, and partnerships. Background in wholesale operations &mdash; built verqo to solve problems he carried on his own P&amp;L." },
            { role: "Operations", title: "David Levy",     sub: "Head of Operations",
              body: "Runs day-to-day operations: onboarding, compliance workflows, customer success. Keeps the platform shipping &amp; the partners happy." },
            { role: "Revenue", title: "Basi Hershkowitz",  sub: "Sales Director",
              body: "Owns the seller &amp; buyer GTM funnel. Walks brands and retailers through the verqo flow and the financial mechanics behind it." },
            { role: "Data &amp; Eng", title: "Dom Byno",   sub: "Data &amp; Automation Expert",
              body: "Owns data, automation, and infrastructure decisions. Designs underwriting models, recon logic, and the integrations behind every flow on the platform." },
          ].map(p => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-400" dangerouslySetInnerHTML={{__html: p.role}}/>
              <div className="text-[18px] font-semibold text-slate-900 mt-1">{p.title}</div>
              <div className="text-[12.5px] text-lime-700 font-medium mt-0.5" dangerouslySetInnerHTML={{__html: p.sub}}/>
              <div className="text-[12.5px] text-slate-600 mt-3 leading-relaxed" dangerouslySetInnerHTML={{__html: p.body}}/>
            </div>
          ))}
        </div>
        <p className="mt-10 text-[12.5px] text-slate-500 max-w-2xl leading-relaxed">
          Verqo runs lean by design. Additional dept hires (Risk &amp; Underwriting, BSA Officer, Engineering) are planned alongside sponsor-bank onboarding &mdash; the standby capital is committed to bring the right people on at the right moment.
        </p>
      </section>

      {/* ─── PARTNERSHIPS ─── */}
      <section id="partners" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>Key partnerships</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          The names we trust behind every transaction.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
          {PARTNERSHIPS.map(p => (
            <div key={p.name} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-900 text-lime-300 grid place-items-center shrink-0">
                <Plug className="w-4 h-4" strokeWidth={1.8}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-slate-900">{p.name}</div>
                <div className="text-[11.5px] text-slate-600 truncate">{p.role}</div>
              </div>
              <span className={`text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                p.status === "live"
                  ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : "text-amber-700 bg-amber-50 border-amber-200"
              }`}>{p.status}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── WHY-NOW + SPONSOR-BANK CTA · moved to AFTER projections (Jun 2026) ─── */}

      {/* ─── 12-MONTH BANK GMV PROJECTIONS (moved to bottom Jun 2026) ─── */}
      <section id="projections" className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Eyebrow>12-month projection · transaction volume through U.S. Bank</Eyebrow>
        <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
          <span className="bg-lime-300 px-3 -rotate-1 inline-block">$1B+</span> in bank-cleared volume in 12 months.
        </h2>
        <p className="mt-5 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
          Every dollar of platform GMV settles through U.S. Bank — ACH pull from buyer, ACH/RTP push to seller, all reconciled across each counterparty's U.S. Bank DDA. This is the volume the bank actually sees and earns interchange / treasury yield on.
        </p>

        {/* Headline KPI cards */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { v: "$1.02B",  l: "12-month bank-cleared volume", sub: "cumulative across all transactions" },
            { v: "~51,000", l: "Settlement transactions",        sub: "ACH/wire originations through bank" },
            { v: "175",     l: "Active sellers by month 12",    sub: "with onboarding ramp" },
            { v: "3,500",   l: "Active buyers by month 12",     sub: "20 per seller on average" },
          ].map(s => (
            <div key={s.l} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 tabular-nums">{s.v}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-1.5">{s.l}</div>
              <div className="text-[11px] text-slate-500 mt-1">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Quarterly ramp */}
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500">Quarterly ramp · cumulative through month 12</div>
              <div className="text-[15px] font-semibold text-slate-900">Path to $1B bank-cleared volume</div>
            </div>
            <LineChart className="w-5 h-5 text-lime-700"/>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 text-[10.5px] mono uppercase tracking-[0.2em] text-slate-500">
                  <th className="text-left p-4 font-medium">Quarter</th>
                  <th className="text-left p-4 font-medium">Active sellers</th>
                  <th className="text-left p-4 font-medium">Active buyers</th>
                  <th className="text-left p-4 font-medium">Quarterly volume</th>
                  <th className="text-left p-4 font-medium">Cumulative volume</th>
                  <th className="text-left p-4 font-medium">Transactions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Q1 · months 1-3",  "15",  "300",   "$45M",  "$45M",     "2,250"],
                  ["Q2 · months 4-6",  "50",  "1,000", "$150M", "$195M",    "7,500"],
                  ["Q3 · months 7-9",  "100", "2,000", "$300M", "$495M",    "15,000"],
                  ["Q4 · months 10-12","175", "3,500", "$525M", "$1,020M",  "26,250"],
                ].map(([q, s, b, qg, cg, t]) => (
                  <tr key={q} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 font-semibold text-slate-900">{q}</td>
                    <td className="p-4 text-slate-700 tabular-nums">{s}</td>
                    <td className="p-4 text-slate-700 tabular-nums">{b}</td>
                    <td className="p-4 text-slate-900 tabular-nums font-medium">{qg}</td>
                    <td className="p-4 text-lime-700 tabular-nums font-semibold">{cg}</td>
                    <td className="p-4 text-slate-700 tabular-nums">{t}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Math / unit economics for the bank */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/40 p-6">
          <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500 mb-3">The unit math</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3 text-[13px] text-slate-700">
            <div><strong className="text-slate-900">$20,000</strong> · average ticket per transaction</div>
            <div><strong className="text-slate-900">$50,000</strong> · average buyer purchase volume per month</div>
            <div><strong className="text-slate-900">20 buyers</strong> · per active seller, on average</div>
            <div><strong className="text-slate-900">$1.0M</strong> · monthly volume per active seller</div>
            <div><strong className="text-slate-900">~50</strong> · settlement events per seller per month</div>
            <div><strong className="text-slate-900">100%</strong> · of platform volume settles through U.S. Bank</div>
          </div>
          <p className="mt-5 pt-4 border-t border-slate-200 text-[11.5px] text-slate-500 leading-relaxed">
            Projections built bottom-up from verified pipeline and signed letters of intent. Updated quarterly as onboarding ramp data accumulates.
          </p>
        </div>
      </section>

      {/* ─── WHY-NOW + SPONSOR-BANK CTA · positioned after projections so it
            closes the diligence narrative with a clear call to action ─── */}
      <section className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="rounded-3xl bg-[#0c1612] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
               style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
          <div className="relative">
            <Eyebrow>Why a sponsor bank should engage</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight">
              Lower regulatory burden.<br/>
              <span className="bg-lime-300 text-slate-900 px-3 -rotate-1 inline-block mt-2">Higher predictable volume.</span>
            </h2>
            <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-4 text-[13.5px]">
              {[
                ["Verqo doesn't custody funds", "We orchestrate. You custody. Lower compliance burden, cleaner audit trail."],
                ["Strong KYB pipeline",         "Middesk-validated tenants only. Every applicant surfaced to bank compliance via /banking-docs."],
                ["Recon-friendly",              "Sponsor-bank ledger reconciles to bank GL daily. Recon reports on request."],
                ["Predictable volume",          "$1B+ bank-cleared volume in 12 months. Detailed ramp shown in the section above."],
              ].map(([t, b]) => (
                <div key={t} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-4 h-4 text-lime-300 mt-1 shrink-0"/>
                  <div>
                    <div className="font-semibold text-white">{t}</div>
                    <div className="text-white/65 mt-1 leading-relaxed">{b}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="/api/banking-docs/download-all"
                 className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-lime-300 text-slate-900 text-[14px] font-semibold hover:bg-lime-400 transition-colors">
                Download full diligence package <Download className="w-4 h-4"/>
              </a>
              <Link to="/banking-docs"
                 className="inline-flex items-center gap-2 px-7 py-4 rounded-lg border border-white/20 text-white text-[14px] hover:bg-white/5 transition-colors">
                Browse all docs <FileText className="w-4 h-4"/>
              </Link>
              <Link to="/contact"
                 className="inline-flex items-center gap-2 px-7 py-4 rounded-lg border border-white/20 text-white text-[14px] hover:bg-white/5 transition-colors">
                Schedule a call <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── (no marketing cross-links — diligence portal must
          not pull banks into the seller/buyer funnels) */}
      <footer className="relative border-t border-slate-200 py-10 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 text-[11.5px] text-slate-500">
          <div className="flex items-center gap-2">
            <VerqoLockup small/>
            <span>· Confidential · For sponsor-bank review only</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/banking-docs" className="hover:text-slate-900">Full doc package</Link>
            <a href="/api/banking-docs/download-all" className="hover:text-slate-900">Download .zip</a>
            <a href="mailto:zev@brnddirect.com" className="hover:text-slate-900">Contact verqo</a>
          </div>
        </div>
        <div className="mt-3 text-[10.5px] text-slate-400">
          Prepared by Verqo. Do not distribute. © {new Date().getFullYear()} Verqo.
        </div>
      </footer>
    </div>
  );
}
